import {
  addMonthsIso,
  isIsoDate,
  monthDays,
  monthWeekday,
  todayIso,
  type DateTimeZone,
} from "@/utils/date/time-zone";
import { recentDateRange, type DateRange } from "@/utils/date/date-range";

export type DatePreset = "today" | "7" | "30" | "60" | "90" | "365" | "custom";

export const DATE_PRESETS = [
  { id: "today" as const, days: 1, labelKey: "Today" },
  { id: "7" as const, days: 7, labelKey: "Last7Days" },
  { id: "30" as const, days: 30, labelKey: "Last30Days" },
  { id: "60" as const, days: 60, labelKey: "Last60Days" },
  { id: "90" as const, days: 90, labelKey: "Last90Days" },
  { id: "365" as const, days: 365, labelKey: "Last365Days" },
] as const;

export function rangeForPreset(
  preset: Exclude<DatePreset, "custom">,
  timeZone: DateTimeZone = "UTC",
): DateRange {
  const days = DATE_PRESETS.find((item) => item.id === preset)?.days ?? 30;
  return recentDateRange(days, timeZone);
}

export function matchPreset(range: DateRange, timeZone: DateTimeZone = "UTC"): DatePreset {
  const today = todayIso(timeZone);
  if (range.end !== today) return "custom";
  for (const preset of DATE_PRESETS) {
    if (range.start === recentDateRange(preset.days, timeZone).start) return preset.id;
  }
  return "custom";
}

export function monthKey(value: string, timeZone: DateTimeZone = "UTC") {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value) && isIsoDate(value)) return value.slice(0, 7);
  return todayIso(timeZone).slice(0, 7);
}

export function shiftMonth(month: string, amount: number) {
  return addMonthsIso(month, amount);
}

export function calendarCells(month: string) {
  if (!/^\d{4}-\d{2}$/.test(month)) return [] as Array<string | null>;
  const cells: Array<string | null> = Array.from({ length: monthWeekday(month) }, () => null);
  for (let day = 1; day <= monthDays(month); day += 1) {
    cells.push(`${month}-${String(day).padStart(2, "0")}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export function clampDateRange(start: string, end: string): DateRange {
  return start <= end ? { start, end } : { start: end, end: start };
}

export type CalendarDayRole = "none" | "single" | "start" | "end" | "inRange";

export function calendarDayRole(date: string, range: DateRange): CalendarDayRole {
  if (!isIsoDate(range.start) || !isIsoDate(range.end)) return "none";
  if (date === range.start && date === range.end) return "single";
  if (date === range.start) return "start";
  if (date === range.end) return "end";
  if (date > range.start && date < range.end) return "inRange";
  return "none";
}

export function pickerViewMonth(range: DateRange, timeZone: DateTimeZone = "UTC") {
  const startMonth = monthKey(range.start, timeZone);
  const endMonth = monthKey(range.end, timeZone);
  if (startMonth === endMonth || startMonth === shiftMonth(endMonth, -1)) return startMonth;
  return shiftMonth(endMonth, -1);
}

export function resolvePickerRange({
  range,
  hoverDate,
  picking,
}: {
  range: DateRange;
  hoverDate?: string | undefined;
  picking: boolean;
}): DateRange {
  if (!picking || !hoverDate || !isIsoDate(range.start) || !isIsoDate(hoverDate)) return range;
  return clampDateRange(range.start, hoverDate);
}

export function isDateAfterToday(date: string, timeZone: DateTimeZone = "UTC") {
  return isIsoDate(date) && date > todayIso(timeZone);
}
